// Core simulation data model (§17.3). Frozen after WP0: additive changes only.
import type { BS, Cause, EK, ItemKind, MapId, ModeId, MutatorId, RoundPhase } from './ids';
import type { SimEvent } from './events';

export interface Vec2 { x: number; y: number }
export interface Vec3 { x: number; y: number; z: number }

/** One tick of player intent. Identical for humans and bots. Encoded in 10 bytes (§20.3). */
export interface InputFrame {
  tick: number;          // room tick this input applies to
  mx: number; my: number;   // move, each in [-1,1], |m| <= 1 (quantized 1/127)
  ax: number; ay: number;   // item/throw aim direction (unnormalized ok); (0,0) = none → use facing
  sx: number; sy: number;   // assisted attack dir for shove/grab; (0,0) = none → facing
  held: number;          // bitmask of Btn
  cShove: number; cGrab: number; cJump: number; cItem: number; // 4-bit wrapping press counters (0..15)
  cHonk: number;         // 3-bit wrapping press counter (0..7)
  emote: number;         // 0 none, 1..4
  cEmote: number;        // 2-bit wrapping counter (0..3)
}
export const Btn = { SHOVE: 1, GRAB: 2, JUMP: 4, ITEM: 8, HONK: 16 } as const;
export const NEUTRAL_INPUT: Omit<InputFrame, 'tick'> = { mx: 0, my: 0, ax: 0, ay: 0, sx: 0, sy: 0, held: 0, cShove: 0, cGrab: 0, cJump: 0, cItem: 0, cHonk: 0, emote: 0, cEmote: 0 };

export interface PrevPress { shove: number; grab: number; jump: number; item: number; honk: number; emote: number; mxSign: number; mySign: number }

export interface Bean {
  slot: number;
  active: boolean;           // has a body this round (false: empty slot)
  team: number;              // 0 FFA, 1 red, 2 blue
  state: BS;
  st: number;                // ticks spent in current state
  pos: Vec2; vel: Vec2;
  z: number; vz: number;
  facing: number;            // radians; 0 = +x, π/2 = +y
  radius: number; mass: number; kbDealt: number; kbTaken: number;   // derived from shake each tick
  dizzy: number; lastHitTick: number;
  cdShove: number; cdGrab: number; cdLedge: number; cdPebble: number; cdCup: number; cdVend: number;
  lungeDir: Vec2; lungeStartTick: number; lungeHitMask: number;
  partner: number;           // CARRYING: victim; GRABBED: carrier; YANKED: other; else NO_SLOT
  escape: number;
  edgePoint: Vec2; edgeNormal: Vec2;   // TEETER/LEDGE/CLIMB anchor; normal points inward
  item: ItemKind; charges: number;
  shakeTicks: number; floatTicks: number; invulnTicks: number; potatoImmuneTicks: number;
  jumpBufferTicks: number; coyoteTicks: number; groundZ: number;
  touchBy: number; touchTick: number; touchCause: Cause; touchAux: number; // touchAux: peel age (ticks) etc.
  out: boolean; place: number; koTick: number; koBy: number;
  respawnTicks: number; crownTicks: number; hasPotato: boolean; holdsCrown: boolean;
  readyTicks: number;        // lobby ready pad progress
  prev: PrevPress;
}

export interface Entity {
  id: number; kind: EK; owner: number;
  pos: Vec2; vel: Vec2; z: number; vz: number;
  t: number;        // age in ticks
  timer: number;    // kind-specific countdown (fuse, drop, life)
  data: number;     // kind-specific (BOMB: carrier slot; PLUNGER: hit slot; CROWN: holder; CRATE: 0; DUMMY/CUBE: home index; PEEL: placedTick low bits)
  data2: number;    // kind-specific (BOMB: landed 0/1; CROWN: no-pick slot; PEBBLE: ghost slot; DUMMY: dizzy)
}

export interface MapState {
  tiltX: number; tiltY: number;               // teacup (radians)
  wedgeFallen: number; wedgeWarn: number;     // teacup bitmasks
  wedgeOrder: number[];                       // teacup SD order (8)
  tileState: number[]; tileTimer: number[];   // cake (37 each)
  sdCursor: number;                           // progression index for SD sequences
  cupOccupant: number;                        // teacup SPUN slot or NO_SLOT
}

export interface ModeState {
  potatoSlot: number; potatoFuse: number; potatoFuseMax: number; potatoNextTick: number;
  potatoPasser: number; potatoPassTick: number; potatoIntensity: number;   // 0..15 (replicated); fuse itself never replicated
  crownEntId: number; crownHolder: number; crownRespawnTick: number;
  matchPoint: boolean;
  aliveAtStart: number;
}

export interface RoundRules {
  accel: number; decel: number; gravity: number; jumpVz: number;
  kbMult: number; dizzyGainMult: number;
  crateMin: number; crateMax: number; crateMaxCount: number;
  itemPool: ItemKind[];                       // allowed items
  bombOnlyCrates: boolean;
}

export interface RoundConfig {
  mapId: MapId; modeId: ModeId; mutator: MutatorId;
  seed: number; golden: boolean; itemsOn: boolean; teamsOn: boolean;
  activeSlots: boolean[];                     // length 8
  teams: number[];                            // length 8
  matchScores: number[];                      // length 8 (for rubber band, potato exclusion, crowned)
  crownedSlot: number;                        // NO_SLOT if none
  startItems: ItemKind[];                     // length 8 (golden round)
  capTickOverride?: number;                   // ?roundSec debug
}

export interface RoundStats {   // per slot, accumulated from events (modes/stats.ts)
  kos: number; ghostKos: number; selfFalls: number; shovesLanded: number; throws: number; throwKos: number;
  grabsEscaped: number; honks: number; clutches: number; ledgeClimbs: number; balloonSaves: number;
  peelSlips: number; peelKos: number; oldestPeelKoTicks: number; pebbleHits: number; clashesWon: number;
  bombKos: number; stomps: number; revenges: number; bounties: number; longestFlight: number;
  crownTicks: number; potatoPasses: number;
}

export interface World {
  tick: number;                // round tick; negative during INTRO
  roundId: number;
  cfg: RoundConfig;
  rules: RoundRules;
  rng: number;                 // mulberry32 state (server-authoritative; not used in predict mode)
  phase: RoundPhase;
  beans: Bean[];               // length MAX_BEANS
  ents: Entity[];
  nextEntId: number;
  map: MapState;
  mode: ModeState;
  crateNextTick: number;
  over: boolean;
  winner: number;              // slot, NO_SLOT for draw/none, 254 = team result (see mode state)
  koOrder: number[];           // slots in KO order
  events: SimEvent[];          // filled during step; caller drains
  hist: LagHistory | null;     // server only
  /** Predict mode (§18.2): the local slot while `stepWorld` runs with `ctx.predictSlot`; unset on the server. */
  predictSlot?: number;
  /** Predict mode: round tick of the snapshot this world was cloned from (set by `cloneWorld`); bounds puppet dead-reckoning. */
  predictBaseTick?: number;
}

export interface LagHistory { ticks: number[]; x: Float32Array; y: Float32Array; z: Float32Array; head: number } // [historyTicks × MAX_BEANS]

export interface StepContext {
  predictSlot: number;         // NO_SLOT on server; local slot when client-predicting
  rewindTicks: number[];       // per attacker slot: how many ticks to rewind targets (server lag comp); zeros on client
}
